import type { DriveManagementCopy } from '@/i18n/drives-management-copy-types';

export const esDriveManagementCopy = {
  description: 'Archivos compartidos para equipos, proyectos y colaboradores.',
  createDrive: 'Crear unidad',
  createDriveTitle: 'Crear una unidad compartida',
  driveName: 'Nombre de la unidad',
  driveNameHint: 'Los nombres son únicos y se convierten en la ruta legible.',
  driveNameTaken: 'Ya existe una unidad llamada «{name}».',
  driveNameReserved: '«{name}» está reservado. Elige otro nombre.',
  driveNameInvalid: 'Usa letras o números en el nombre de la unidad.',
  driveNameSuggestions: 'Disponibles:',
  driveDescription: 'Descripción (opcional)',
  governance: 'Unidad para',
  curatorOptIn: 'Hacerme Administrador de la biblioteca',
  workspace: 'Espacio de trabajo',
  teamGoverned: 'Equipo',
  projectGoverned: 'Proyecto',
  selectTeam: 'Selecciona un equipo',
  selectProject: 'Selecciona un proyecto',
  projectVisibility: 'Visibilidad del proyecto',
  assignedMembers: 'Miembros asignados',
  workspaceVisible: 'Visible en el espacio de trabajo',
  customAudience: 'Audiencia personalizada',
  primaryProjectDrive: 'Convertir en la unidad principal del proyecto',
  create: 'Crear',
  cancel: 'Cancelar',
  loading: 'Cargando unidades…',
  emptyTitle: 'No hay unidades disponibles',
  emptyDescription:
    'Crea una unidad compartida o pide acceso a un Administrador.',
  openDrive: 'Abrir unidad',
  settings: 'Configuración de la unidad',
  members: 'Miembros',
  links: 'Vínculos',
  permissions: 'Permisos',
  workflow: 'Flujo de trabajo',
  activity: 'Actividad',
  storage: 'Almacenamiento',
  role: 'Rol',
  status: 'Estado',
  usage: 'Almacenamiento usado',
  restrictedFolders: 'Carpetas restringidas',
  guestManagement: 'Gestión de invitados',
  createFailed: 'No se pudo crear la unidad.',
  addMember: 'Añadir miembro',
  removeMember: 'Quitar',
  selectMember: 'Selecciona un miembro del espacio',
  noDirectMembers: 'Aún no hay miembros directos.',
  unknownMember: 'Miembro desconocido',
  loadMoreMembers: 'Cargar más miembros',
  memberAdded: 'Miembro añadido.',
  memberRemoved: 'Miembro eliminado.',
  membersFailed: 'No se pudieron actualizar los miembros de la unidad.',
  managerOnly:
    'Solo los Administradores de la unidad pueden gestionar miembros.',
  linkType: 'Tipo de vínculo',
  selectLinkTarget: 'Selecciona un equipo o proyecto',
  addLink: 'Añadir vínculo',
  removeLink: 'Quitar',
  noLinks: 'Esta unidad no tiene vínculos de equipo ni de proyecto.',
  unknownLink: 'Contexto no disponible',
  linkAdded: 'Vínculo de unidad añadido.',
  linkRemoved: 'Vínculo de unidad eliminado.',
  linksFailed: 'No se pudieron actualizar los vínculos de la unidad.',
  viewerDownloads: 'Permitir descargas a los lectores',
  publicLinksWorkspacePolicy:
    'Los vínculos públicos siguen sujetos a la política del propietario del espacio.',
  selectFolder: 'Selecciona una carpeta',
  restrict: 'Restringir',
  removeRestriction: 'Quitar límite',
  boundaryCreated: 'Se creó el límite de carpeta restringida.',
  boundaryRemoved: 'Se eliminó el límite de carpeta restringida.',
  createWorkflow: 'Crear flujo predeterminado',
  workflowCreated: 'Flujo predeterminado creado.',
  noWorkflows: 'No hay un flujo configurado para esta unidad.',
  noActivity: 'Aún no hay actividad en la unidad.',
  activityAccess: 'Acceso modificado',
  activityContent: 'Actividad de contenido',
  activityWorkflow: 'Actividad del flujo de trabajo',
  activityPublish: 'Actividad de publicación',
  activityGovernance: 'Configuración de la unidad actualizada',
  activityRecovery: 'Actividad de recuperación',
  activitySecurity: 'Actividad de seguridad',
  activityAutomation: 'Actividad del agente',
  activityDrive: 'Unidad actualizada',
  policyUpdated: 'Política de la unidad actualizada.',
  settingsFailed: 'No se pudo actualizar la configuración de la unidad.',
  guestEmail: 'Correo de la cuenta invitada',
  addGuest: 'Añadir invitado',
  guestAdded: 'Invitado añadido como comentarista.',
  guestFailed:
    'No se pudo añadir al invitado. Debe tener ya una identidad de IMAI.',
  guestExpiry:
    'El acceso de invitado caduca a los 90 días de forma predeterminada y se limita a Colaborador.',
  recovery: 'Recuperación',
  contentAccessRequired: 'Se requiere acceso al contenido',
  recoveryDescription:
    'Asigna un Administrador sustituto sin abrir ni ver el contenido de la unidad.',
  replacementManager: 'Selecciona un Administrador sustituto',
  assignManager: 'Asignar Administrador',
  recoveryAssigned: 'Administrador sustituto asignado.',
  recoveryFailed: 'No se pudo asignar al Administrador sustituto.',
  publishToLibrary: 'Publicar en la unidad del espacio',
  publish: 'Publicar',
  publishStarted: 'Se inició la copia gestionada de la biblioteca.',
  publishFailed: 'No se pudo publicar este archivo.',
  noPublishableFiles: 'Esta unidad no contiene archivos para publicar.',
  libraryCuratorRequired:
    'Se requiere acceso de Gestor de contenido de la biblioteca para publicar.',
} satisfies DriveManagementCopy;

export const frDriveManagementCopy = {
  description:
    'Fichiers partagés pour les équipes, les projets et les collaborateurs.',
  createDrive: 'Créer un espace',
  createDriveTitle: 'Créer un espace partagé',
  driveName: 'Nom de l’espace',
  driveNameHint:
    'Les noms sont uniques et deviennent le chemin lisible du dossier.',
  driveNameTaken: 'Un espace nommé « {name} » existe déjà.',
  driveNameReserved: '« {name} » est réservé. Choisissez un autre nom.',
  driveNameInvalid:
    'Utilisez des lettres ou des chiffres dans le nom de l’espace.',
  driveNameSuggestions: 'Disponibles :',
  driveDescription: 'Description (facultative)',
  governance: 'Espace pour',
  curatorOptIn: 'Me désigner Gestionnaire de la bibliothèque',
  workspace: 'Espace de travail',
  teamGoverned: 'Équipe',
  projectGoverned: 'Projet',
  selectTeam: 'Sélectionner une équipe',
  selectProject: 'Sélectionner un projet',
  projectVisibility: 'Visibilité du projet',
  assignedMembers: 'Membres affectés',
  workspaceVisible: 'Visible dans l’espace de travail',
  customAudience: 'Audience personnalisée',
  primaryProjectDrive: 'Définir comme espace principal du projet',
  create: 'Créer',
  cancel: 'Annuler',
  loading: 'Chargement des espaces…',
  emptyTitle: 'Aucun espace disponible',
  emptyDescription:
    'Créez un espace partagé ou demandez l’accès à un Gestionnaire.',
  openDrive: 'Ouvrir l’espace',
  settings: 'Paramètres de l’espace',
  members: 'Membres',
  links: 'Liens',
  permissions: 'Autorisations',
  workflow: 'Flux de travail',
  activity: 'Activité',
  storage: 'Stockage',
  role: 'Rôle',
  status: 'Statut',
  usage: 'Stockage utilisé',
  restrictedFolders: 'Dossiers restreints',
  guestManagement: 'Gestion des invités',
  createFailed: 'Impossible de créer l’espace.',
  addMember: 'Ajouter un membre',
  removeMember: 'Retirer',
  selectMember: 'Sélectionner un membre de l’espace de travail',
  noDirectMembers: 'Aucun membre direct pour le moment.',
  unknownMember: 'Membre inconnu',
  loadMoreMembers: 'Charger plus de membres',
  memberAdded: 'Membre ajouté.',
  memberRemoved: 'Membre retiré.',
  membersFailed: 'Impossible de mettre à jour les membres de l’espace.',
  managerOnly: 'Seuls les Gestionnaires de l’espace peuvent gérer les membres.',
  linkType: 'Type de lien',
  selectLinkTarget: 'Sélectionner une équipe ou un projet',
  addLink: 'Ajouter un lien',
  removeLink: 'Retirer',
  noLinks: 'Cet espace n’est lié à aucune équipe ni aucun projet.',
  unknownLink: 'Contexte indisponible',
  linkAdded: 'Lien de l’espace ajouté.',
  linkRemoved: 'Lien de l’espace retiré.',
  linksFailed: 'Impossible de mettre à jour les liens de l’espace.',
  viewerDownloads: 'Autoriser les lecteurs à télécharger',
  publicLinksWorkspacePolicy:
    'Les liens publics restent régis par la politique du propriétaire de l’espace de travail.',
  selectFolder: 'Sélectionner un dossier',
  restrict: 'Restreindre',
  removeRestriction: 'Supprimer la limite',
  boundaryCreated: 'Limite de dossier restreint créée.',
  boundaryRemoved: 'Limite de dossier restreint supprimée.',
  createWorkflow: 'Créer le flux par défaut',
  workflowCreated: 'Flux par défaut créé.',
  noWorkflows: 'Aucun flux de travail n’est configuré pour cet espace.',
  noActivity: 'Aucune activité dans l’espace pour le moment.',
  activityAccess: 'Accès modifié',
  activityContent: 'Activité du contenu',
  activityWorkflow: 'Activité du flux de travail',
  activityPublish: 'Activité de publication',
  activityGovernance: 'Paramètres de l’espace mis à jour',
  activityRecovery: 'Activité de récupération',
  activitySecurity: 'Activité de sécurité',
  activityAutomation: 'Activité de l’agent',
  activityDrive: 'Espace mis à jour',
  policyUpdated: 'Politique de l’espace mise à jour.',
  settingsFailed: 'Impossible de mettre à jour les paramètres de l’espace.',
  guestEmail: 'Adresse e-mail du compte invité',
  addGuest: 'Ajouter un invité',
  guestAdded: 'Invité ajouté comme commentateur.',
  guestFailed:
    'Impossible d’ajouter cet invité. Une identité IMAI doit déjà exister.',
  guestExpiry:
    'L’accès invité expire par défaut après 90 jours et ne peut dépasser le rôle Contributeur.',
  recovery: 'Récupération',
  contentAccessRequired: 'Accès au contenu requis',
  recoveryDescription:
    'Désignez un Gestionnaire de remplacement sans ouvrir ni consulter le contenu.',
  replacementManager: 'Sélectionner un Gestionnaire de remplacement',
  assignManager: 'Désigner le Gestionnaire',
  recoveryAssigned: 'Gestionnaire de remplacement désigné.',
  recoveryFailed: 'Impossible de désigner le Gestionnaire de remplacement.',
  publishToLibrary: 'Publier dans l’espace du workspace',
  publish: 'Publier',
  publishStarted: 'La copie gérée de la bibliothèque a démarré.',
  publishFailed: 'Impossible de publier ce fichier.',
  noPublishableFiles: 'Cet espace ne contient aucun fichier à publier.',
  libraryCuratorRequired:
    'Le rôle Gestionnaire de contenu de la bibliothèque est requis pour publier.',
} satisfies DriveManagementCopy;

export const hiDriveManagementCopy = {
  description: 'टीमों, प्रोजेक्ट और सहयोगियों के लिए साझा फ़ाइलें।',
  createDrive: 'ड्राइव बनाएँ',
  createDriveTitle: 'साझा ड्राइव बनाएँ',
  driveName: 'ड्राइव का नाम',
  driveNameHint: 'ड्राइव नाम खास होता है और पढ़ने योग्य फ़ोल्डर पथ बनता है।',
  driveNameTaken: '“{name}” नाम की ड्राइव पहले से मौजूद है।',
  driveNameReserved: '“{name}” आरक्षित है। कोई दूसरा नाम चुनें।',
  driveNameInvalid: 'ड्राइव नाम में अक्षर या अंक इस्तेमाल करें।',
  driveNameSuggestions: 'उपलब्ध:',
  driveDescription: 'विवरण (वैकल्पिक)',
  governance: 'ड्राइव किसके लिए है',
  curatorOptIn: 'मुझे लाइब्रेरी मैनेजर बनाएँ',
  workspace: 'वर्कस्पेस',
  teamGoverned: 'टीम',
  projectGoverned: 'प्रोजेक्ट',
  selectTeam: 'टीम चुनें',
  selectProject: 'प्रोजेक्ट चुनें',
  projectVisibility: 'प्रोजेक्ट दृश्यता',
  assignedMembers: 'असाइन किए गए सदस्य',
  workspaceVisible: 'वर्कस्पेस में दिखाई दे',
  customAudience: 'कस्टम दर्शक',
  primaryProjectDrive: 'इसे प्रोजेक्ट की प्राथमिक ड्राइव बनाएँ',
  create: 'बनाएँ',
  cancel: 'रद्द करें',
  loading: 'ड्राइव लोड हो रही हैं…',
  emptyTitle: 'कोई ड्राइव उपलब्ध नहीं',
  emptyDescription: 'साझा ड्राइव बनाएँ या मैनेजर से एक्सेस माँगें।',
  openDrive: 'ड्राइव खोलें',
  settings: 'ड्राइव सेटिंग',
  members: 'सदस्य',
  links: 'लिंक',
  permissions: 'अनुमतियाँ',
  workflow: 'वर्कफ़्लो',
  activity: 'गतिविधि',
  storage: 'स्टोरेज',
  role: 'भूमिका',
  status: 'स्थिति',
  usage: 'उपयोग किया गया स्टोरेज',
  restrictedFolders: 'प्रतिबंधित फ़ोल्डर',
  guestManagement: 'अतिथि प्रबंधन',
  createFailed: 'ड्राइव नहीं बनाई जा सकी।',
  addMember: 'सदस्य जोड़ें',
  removeMember: 'हटाएँ',
  selectMember: 'वर्कस्पेस सदस्य चुनें',
  noDirectMembers: 'अभी कोई प्रत्यक्ष सदस्य नहीं है।',
  unknownMember: 'अज्ञात सदस्य',
  loadMoreMembers: 'और सदस्य लोड करें',
  memberAdded: 'सदस्य जोड़ा गया।',
  memberRemoved: 'सदस्य हटाया गया।',
  membersFailed: 'ड्राइव के सदस्य अपडेट नहीं किए जा सके।',
  managerOnly: 'केवल ड्राइव मैनेजर सदस्य प्रबंधित कर सकते हैं।',
  linkType: 'लिंक प्रकार',
  selectLinkTarget: 'टीम या प्रोजेक्ट चुनें',
  addLink: 'लिंक जोड़ें',
  removeLink: 'हटाएँ',
  noLinks: 'यह ड्राइव किसी टीम या प्रोजेक्ट से लिंक नहीं है।',
  unknownLink: 'अनुपलब्ध संदर्भ',
  linkAdded: 'ड्राइव लिंक जोड़ा गया।',
  linkRemoved: 'ड्राइव लिंक हटाया गया।',
  linksFailed: 'ड्राइव लिंक अपडेट नहीं किए जा सके।',
  viewerDownloads: 'दर्शक को डाउनलोड करने दें',
  publicLinksWorkspacePolicy:
    'सार्वजनिक लिंक पर वर्कस्पेस मालिक की नीति लागू रहती है।',
  selectFolder: 'फ़ोल्डर चुनें',
  restrict: 'प्रतिबंधित करें',
  removeRestriction: 'सीमा हटाएँ',
  boundaryCreated: 'प्रतिबंधित फ़ोल्डर सीमा बनाई गई।',
  boundaryRemoved: 'प्रतिबंधित फ़ोल्डर सीमा हटाई गई।',
  createWorkflow: 'डिफ़ॉल्ट वर्कफ़्लो बनाएँ',
  workflowCreated: 'डिफ़ॉल्ट वर्कफ़्लो बनाया गया।',
  noWorkflows: 'इस ड्राइव के लिए कोई वर्कफ़्लो कॉन्फ़िगर नहीं है।',
  noActivity: 'अभी कोई ड्राइव गतिविधि नहीं है।',
  activityAccess: 'एक्सेस बदला गया',
  activityContent: 'कंटेंट गतिविधि',
  activityWorkflow: 'वर्कफ़्लो गतिविधि',
  activityPublish: 'प्रकाशन गतिविधि',
  activityGovernance: 'ड्राइव सेटिंग अपडेट की गईं',
  activityRecovery: 'रिकवरी गतिविधि',
  activitySecurity: 'सुरक्षा गतिविधि',
  activityAutomation: 'एजेंट गतिविधि',
  activityDrive: 'ड्राइव अपडेट की गई',
  policyUpdated: 'ड्राइव नीति अपडेट की गई।',
  settingsFailed: 'ड्राइव सेटिंग अपडेट नहीं की जा सकी।',
  guestEmail: 'अतिथि खाते का ईमेल',
  addGuest: 'अतिथि जोड़ें',
  guestAdded: 'अतिथि को टिप्पणीकार के रूप में जोड़ा गया।',
  guestFailed: 'अतिथि नहीं जोड़ा जा सका। उसकी IMAI पहचान पहले से होनी चाहिए।',
  guestExpiry:
    'अतिथि एक्सेस डिफ़ॉल्ट रूप से 90 दिन बाद समाप्त होता है और योगदानकर्ता तक सीमित है।',
  recovery: 'रिकवरी',
  contentAccessRequired: 'कंटेंट एक्सेस आवश्यक है',
  recoveryDescription:
    'ड्राइव कंटेंट खोले या देखे बिना प्रतिस्थापन मैनेजर असाइन करें।',
  replacementManager: 'प्रतिस्थापन मैनेजर चुनें',
  assignManager: 'मैनेजर असाइन करें',
  recoveryAssigned: 'प्रतिस्थापन मैनेजर असाइन किया गया।',
  recoveryFailed: 'प्रतिस्थापन मैनेजर असाइन नहीं किया जा सका।',
  publishToLibrary: 'वर्कस्पेस ड्राइव में प्रकाशित करें',
  publish: 'प्रकाशित करें',
  publishStarted: 'प्रबंधित लाइब्रेरी कॉपी शुरू हुई।',
  publishFailed: 'यह फ़ाइल प्रकाशित नहीं की जा सकी।',
  noPublishableFiles: 'इस ड्राइव में प्रकाशित करने योग्य फ़ाइलें नहीं हैं।',
  libraryCuratorRequired:
    'प्रकाशित करने के लिए लाइब्रेरी कंटेंट मैनेजर एक्सेस आवश्यक है।',
} satisfies DriveManagementCopy;
